/**
 * Mic state-change cues. Uses CodeBaby's own `long_start.mp3` /
 * `long_finish.mp3` — the same sounds the reference UIs play on
 * `na-microphoneReady` / `na-microphoneStopped`. Files are mirrored into
 * `/public/sounds/` so we don't depend on the CodeBaby CDN at runtime.
 *
 * Why HTMLAudioElement and not Web Audio API: the reference UIs use the
 * exact same approach (a hidden `<audio>` element + `.play()`), and these
 * specific sound files are what Michelle expects — generated chimes
 * sounded different and were the wrong call.
 *
 * Lazy-init: we don't preload until first use so we don't pay the network
 * cost on app start. Subsequent plays reuse the same Audio instance.
 */

let startAudio: HTMLAudioElement | null = null;
let stopAudio: HTMLAudioElement | null = null;

function getAudio(which: 'start' | 'stop'): HTMLAudioElement | null {
  if (typeof window === 'undefined' || typeof Audio === 'undefined') return null;
  if (which === 'start') {
    if (!startAudio) {
      startAudio = new Audio('/sounds/mic-start.mp3');
      startAudio.preload = 'auto';
      startAudio.volume = 0.6;
    }
    return startAudio;
  }
  if (!stopAudio) {
    stopAudio = new Audio('/sounds/mic-stop.mp3');
    stopAudio.preload = 'auto';
    stopAudio.volume = 0.6;
  }
  return stopAudio;
}

function playFromStart(audio: HTMLAudioElement | null) {
  if (!audio) return;
  try {
    // Rewind so a rapid second play (e.g., quick mic toggle) actually
    // plays from the beginning rather than continuing where we left off.
    audio.currentTime = 0;
  } catch {
    /* some browsers throw on currentTime before metadata loads — fine */
  }
  // play() returns a Promise that rejects on autoplay-policy denial. We
  // never want a play failure to bubble up and break the mic flow.
  audio.play().catch(() => {});
}

/** Fires when the loader's mic opens (na-microphoneReady). */
export function playMicStart() {
  playFromStart(getAudio('start'));
}

/** Fires when the loader's mic closes (na-microphoneStopped). */
export function playMicStop() {
  playFromStart(getAudio('stop'));
}
